import type { DayId, DayKind } from '../calendar.ts'
import { addDays, baseDayKind, diffDays } from '../calendar.ts'
import type { DomainEvent } from '../events.ts'
import type { MemberId, StepId, TaskId } from '../ids.ts'
import {
  EMPTY_MEMBER_DAY,
  EMPTY_STATE,
  EMPTY_TASK_DAY,
  type Day,
  type FamilyState,
  type Member,
  type Purchase,
  type Reward,
  type Task,
  type TaskDay,
} from '../state.ts'

/**
 * Replays the log into the current state of the house.
 *
 * The whole log, every time. At around fifty events a day this stays in the
 * single-digit milliseconds for years, and the simplicity is worth more than the
 * speed: there is exactly one way the state can come to be, so a bug in here
 * shows up in every test rather than only on the incremental path.
 */

interface DraftMemberDay {
  tasks: Map<TaskId, TaskDay>
  chestOpened: boolean
  chestMinutes: number
  extraMinutes: number
  paused: boolean
  timerEndsAt: string | undefined
  timerMinutes: number
}

interface DraftDay {
  kind: DayKind
  members: Map<MemberId, DraftMemberDay>
}

interface Draft {
  family: FamilyState['family']
  members: Map<MemberId, Member>
  tasks: Map<TaskId, Task>
  rewards: Map<Reward['id'], Reward>
  days: Map<DayId, DraftDay>
  purchases: Map<Purchase['id'], Purchase>
  seenCommands: Set<string>
  eventCount: number
}

function draftDay(draft: Draft, day: DayId): DraftDay {
  let existing = draft.days.get(day)
  if (!existing) {
    existing = { kind: baseDayKind(day), members: new Map() }
    draft.days.set(day, existing)
  }
  return existing
}

function draftMemberDay(draft: Draft, day: DayId, memberId: MemberId): DraftMemberDay {
  const target = draftDay(draft, day)
  let existing = target.members.get(memberId)
  if (!existing) {
    existing = { ...EMPTY_MEMBER_DAY, tasks: new Map() }
    target.members.set(memberId, existing)
  }
  return existing
}

function draftTaskDay(
  draft: Draft,
  day: DayId,
  memberId: MemberId,
  taskId: TaskId,
): TaskDay & { steps: Set<StepId> } {
  const member = draftMemberDay(draft, day, memberId)
  const existing = member.tasks.get(taskId)
  if (existing) return existing as TaskDay & { steps: Set<StepId> }
  const created = { ...EMPTY_TASK_DAY, steps: new Set<StepId>() }
  member.tasks.set(taskId, created)
  return created
}

function setTaskDay(
  draft: Draft,
  day: DayId,
  memberId: MemberId,
  taskId: TaskId,
  patch: Partial<TaskDay>,
): void {
  const current = draftTaskDay(draft, day, memberId, taskId)
  draftMemberDay(draft, day, memberId).tasks.set(taskId, { ...current, ...patch })
}

function grant(draft: Draft, memberId: MemberId, coins: number, xp: number): void {
  const member = draft.members.get(memberId)
  if (!member) return
  draft.members.set(memberId, { ...member, coins: member.coins + coins, xp: member.xp + xp })
}

export function project(events: Iterable<DomainEvent>): FamilyState {
  const draft: Draft = {
    family: EMPTY_STATE.family,
    members: new Map(),
    tasks: new Map(),
    rewards: new Map(),
    days: new Map(),
    purchases: new Map(),
    seenCommands: new Set(),
    eventCount: 0,
  }

  for (const event of events) {
    draft.eventCount += 1
    apply(draft, event)
  }

  const days = new Map<DayId, Day>()
  for (const [id, value] of draft.days) days.set(id, value)

  return {
    family: draft.family,
    members: draft.members,
    tasks: draft.tasks,
    rewards: draft.rewards,
    days,
    purchases: draft.purchases,
    seenCommands: draft.seenCommands,
    eventCount: draft.eventCount,
  }
}

function apply(draft: Draft, event: DomainEvent): void {
  switch (event.type) {
    case 'FamilyConfigured': {
      // Por lo mismo que las altas de miembros y tareas: al importar una copia en
      // una tablet que ya se había configurado, la configuración nueva —posterior—
      // pisaría la zona horaria y la hora de corte de la casa de verdad. Si algún
      // día hace falta cambiarlas, será con su propio evento, como aquí se hace
      // todo lo demás: no se reutiliza un alta para modificar.
      if (draft.family) return
      const { familyId, timezone, dayCutoffHour, meetingDayOfWeek } = event.payload
      draft.family = { id: familyId, timezone, dayCutoffHour, meetingDayOfWeek }
      return
    }

    /**
     * Dar de alta a alguien que ya está **no lo vuelve a crear**.
     *
     * Aquí estaba el fallo que hacía que importar una copia no sumara las
     * monedas. Los identificadores salían del nombre, así que dos tablets con el
     * mismo hijo generaban el mismo `m-gabriel`; al importar, los eventos viejos
     * se reproducían primero —van por instante— y el alta de la tablet nueva, que
     * es posterior, volvía a escribir el miembro entero con `coins: 0` y `xp: 0`.
     * Un año de monedas se perdía en la última línea del replay, sin un error por
     * ningún lado.
     *
     * La regla vale para todo el replay y no solo para importar: reproducir un
     * alta repetida no puede borrar lo que vino después de ella.
     */
    case 'MemberAdded': {
      const p = event.payload
      if (draft.members.has(p.memberId)) return
      draft.members.set(p.memberId, {
        id: p.memberId,
        name: p.name,
        role: p.role,
        birthYear: p.birthYear,
        accent: p.accent,
        emoji: p.emoji,
        coinWord: undefined,
        reminders: [],
        adaptations: p.adaptations,
        limits: p.limits,
        glutenFree: p.glutenFree,
        xp: 0,
        coins: 0,
        archived: false,
      })
      return
    }

    case 'MemberArchived': {
      const member = draft.members.get(event.payload.memberId)
      if (member) draft.members.set(member.id, { ...member, archived: true })
      return
    }

    case 'MemberRestored': {
      const member = draft.members.get(event.payload.memberId)
      if (member) draft.members.set(member.id, { ...member, archived: false })
      return
    }

    case 'MemberAdaptationsUpdated': {
      const member = draft.members.get(event.payload.memberId)
      if (member) {
        draft.members.set(member.id, { ...member, adaptations: event.payload.adaptations })
      }
      return
    }

    case 'TaskDefined': {
      const p = event.payload
      // Por lo mismo que el alta de un miembro: si esta tarea ya existe, el alta
      // repetida no puede deshacer el nombre que se cambió, el peldaño al que
      // subió ni las monedas que se le retocaron después.
      if (draft.tasks.has(p.taskId)) return
      draft.tasks.set(p.taskId, {
        id: p.taskId,
        memberId: p.memberId,
        kind: p.kind,
        moment: p.moment,
        name: p.name,
        emoji: p.emoji,
        steps: p.steps,
        difficulty: p.difficulty,
        rewards: p.rewards,
        promotionThreshold: p.promotionThreshold,
        requiresPhoto: p.requiresPhoto,
        referencePhotoId: undefined,
        rung: p.initialRung ?? 1,
        game: p.game,
        archived: false,
        changedOn: undefined,
      })
      return
    }

    case 'TaskStepsUpdated': {
      const task = draft.tasks.get(event.payload.taskId)
      if (task) {
        draft.tasks.set(task.id, { ...task, steps: event.payload.steps, changedOn: event.day })
      }
      return
    }

    case 'TaskRenamed': {
      const task = draft.tasks.get(event.payload.taskId)
      if (task) {
        draft.tasks.set(task.id, {
          ...task,
          name: event.payload.name,
          emoji: event.payload.emoji ?? task.emoji,
          changedOn: event.day,
        })
      }
      return
    }

    case 'TaskRestored': {
      const task = draft.tasks.get(event.payload.taskId)
      if (task) draft.tasks.set(task.id, { ...task, archived: false })
      return
    }

    case 'TaskRewardsChanged': {
      const task = draft.tasks.get(event.payload.taskId)
      if (task) {
        // `changedOn` es lo que hace que el niño que necesita avisos lea la
        // tarea otra vez antes de empezarla. Cambiar lo que paga cuenta.
        draft.tasks.set(task.id, {
          ...task,
          rewards: event.payload.rewards,
          changedOn: event.day,
        })
      }
      return
    }

    case 'RemindersSet': {
      const member = draft.members.get(event.payload.memberId)
      if (member) draft.members.set(member.id, { ...member, reminders: event.payload.avisos })
      return
    }

    case 'MemberLookChanged': {
      const member = draft.members.get(event.payload.memberId)
      if (member) {
        draft.members.set(member.id, {
          ...member,
          accent: event.payload.accent,
          emoji: event.payload.emoji,
          coinWord: event.payload.coinWord,
        })
      }
      return
    }

    case 'TaskReferencePhotoSet': {
      const task = draft.tasks.get(event.payload.taskId)
      if (task) draft.tasks.set(task.id, { ...task, referencePhotoId: event.payload.photoId })
      return
    }

    case 'TaskArchived': {
      const task = draft.tasks.get(event.payload.taskId)
      if (task) draft.tasks.set(task.id, { ...task, archived: true })
      return
    }

    case 'RewardDefined': {
      const p = event.payload
      // Igual: un alta repetida no devuelve el precio viejo ni resucita un premio
      // que se había quitado de la tienda.
      if (draft.rewards.has(p.rewardId)) return
      draft.rewards.set(p.rewardId, {
        id: p.rewardId,
        name: p.name,
        price: p.price,
        kind: p.kind,
        memberIds: p.memberIds,
        notes: p.notes,
        limit: p.limit,
        active: true,
      })
      return
    }

    case 'RewardMembersChanged': {
      const reward = draft.rewards.get(event.payload.rewardId)
      if (reward) draft.rewards.set(reward.id, { ...reward, memberIds: event.payload.memberIds })
      return
    }

    case 'RewardArchived': {
      const reward = draft.rewards.get(event.payload.rewardId)
      if (reward) draft.rewards.set(reward.id, { ...reward, active: false })
      return
    }

    case 'PriceChangeScheduled': {
      // A scheduled change only bites on its day, so applying it needs the day
      // the log is being read *as of*. Phase 2 owns that; for now the price
      // moves when the event is replayed, which is the day it was decided for.
      const reward = draft.rewards.get(event.payload.rewardId)
      if (reward) draft.rewards.set(reward.id, { ...reward, price: event.payload.newPrice })
      return
    }

    case 'LimitChangeScheduled': {
      const member = draft.members.get(event.payload.memberId)
      if (member) draft.members.set(member.id, { ...member, limits: event.payload.limits })
      return
    }

    case 'DayTypeDeclared': {
      draftDay(draft, event.payload.day).kind = event.payload.kind
      return
    }

    case 'DayRangeDeclared': {
      // El tramo se guarda como tramo en el registro y se despliega aquí. Un
      // día declarado después por separado gana, porque se lee más tarde: la
      // última decisión es la que vale, que es como funciona todo lo demás.
      const { from, to, kind } = event.payload
      for (let dia = from; dia <= to; dia = addDays(dia, 1)) {
        // Declarar un tramo como «día de colegio» quiere decir «estos son días
        // normales», y un sábado normal es fin de semana. Sin esto, deshacer
        // unas vacaciones convertiría sus sábados en días de colegio y les
        // quitaría quince minutos, que es justo lo que no se hace aquí.
        draftDay(draft, dia).kind = kind === 'weekday' ? baseDayKind(dia) : kind
        // Un tramo mal puesto no puede colgar la app. Un curso son unos
        // doscientos días; mil es un fichero equivocado.
        if (diffDays(from, dia) > 1000) break
      }
      return
    }

    case 'DayPaused': {
      draftMemberDay(draft, event.payload.day, event.payload.memberId).paused = true
      return
    }

    case 'StepCompleted': {
      const { taskId, memberId, stepId } = event.payload
      draftTaskDay(draft, event.day, memberId, taskId).steps.add(stepId)
      return
    }

    case 'StepUncompleted': {
      const { taskId, memberId, stepId } = event.payload
      const current = draftTaskDay(draft, event.day, memberId, taskId)
      current.steps.delete(stepId)
      // A task cannot be waiting to be checked while one of its steps is not
      // done. This is also how a child undoes a sibling's prank without needing
      // an adult: untick anything, and the task drops out of the queue.
      if (current.status === 'marked') {
        setTaskDay(draft, event.day, memberId, taskId, {
          status: 'pending',
          attempt: current.attempt + 1,
        })
      }
      return
    }

    case 'TaskMarkedDone': {
      const { taskId, memberId, commandId } = event.payload
      draft.seenCommands.add(commandId)
      setTaskDay(draft, event.day, memberId, taskId, {
        status: 'marked',
        photoId: event.payload.photoId,
        returnedNote: undefined,
      })
      return
    }

    case 'TaskValidated': {
      const { taskId, memberId, commandId, coins, xp } = event.payload
      draft.seenCommands.add(commandId)
      setTaskDay(draft, event.day, memberId, taskId, { status: 'validated', coins, xp })
      grant(draft, memberId, coins, xp)
      return
    }

    case 'TaskReturned': {
      const { taskId, memberId, note } = event.payload
      const current = draftTaskDay(draft, event.day, memberId, taskId)
      // Nothing is taken away: the task simply goes back to pending, and the
      // attempt count moves on so the child can say "done" a second time.
      setTaskDay(draft, event.day, memberId, taskId, {
        status: 'pending',
        returnedNote: note,
        attempt: current.attempt + 1,
      })
      return
    }

    case 'ValidationRevoked': {
      const { taskId, memberId } = event.payload
      const current = draftTaskDay(draft, event.day, memberId, taskId)
      setTaskDay(draft, event.day, memberId, taskId, {
        status: 'marked',
        coins: 0,
        xp: 0,
        attempt: current.attempt + 1,
      })
      // Two limits, both deliberate. It claws back at most what this one
      // validation gave, and never more than the child still has: you cannot
      // take back coins that have already been spent on something. Experience is
      // untouched, because the work was still done.
      const balance = draft.members.get(memberId)?.coins ?? 0
      grant(draft, memberId, -Math.min(current.coins, balance), 0)
      return
    }

    /**
     * La sesión no cambia el estado de la casa: no da monedas ni experiencia, y
     * lo que paga es la validación de la misión, que viene en su propio evento.
     * Solo se apunta el mando para no contarla dos veces — el historial de
     * intentos se lee del registro, que es donde de verdad está.
     */
    case 'PracticeFinished': {
      draft.seenCommands.add(event.payload.commandId)
      return
    }

    case 'ChestOpened': {
      const member = draftMemberDay(draft, event.day, event.payload.memberId)
      member.chestOpened = true
      member.chestMinutes = event.payload.minutes
      return
    }

    case 'PurchaseMade': {
      const p = event.payload
      draft.seenCommands.add(p.commandId)
      draft.purchases.set(p.purchaseId, {
        id: p.purchaseId,
        memberId: p.memberId,
        rewardId: p.rewardId,
        day: event.day,
        price: p.price,
        quantity: p.quantity,
        status: 'pending',
        grantedMinutes: 0,
      })
      grant(draft, p.memberId, -(p.price * p.quantity), 0)
      return
    }

    case 'PurchaseCancelled': {
      const purchase = draft.purchases.get(event.payload.purchaseId)
      if (!purchase) return
      // Cancelled, not deleted: the log keeps saying that it was wanted once,
      // which is worth knowing at the weekly meeting.
      draft.purchases.set(purchase.id, { ...purchase, status: 'cancelled' })
      grant(draft, event.payload.memberId, event.payload.refund, 0)
      return
    }

    case 'PurchaseGranted': {
      const purchase = draft.purchases.get(event.payload.purchaseId)
      if (!purchase) return
      draft.purchases.set(purchase.id, {
        ...purchase,
        status: 'granted',
        grantedMinutes: event.payload.minutes,
      })
      const member = draftMemberDay(draft, purchase.day, purchase.memberId)
      member.extraMinutes += event.payload.minutes
      return
    }

    case 'PurchaseDelivered': {
      const purchase = draft.purchases.get(event.payload.purchaseId)
      if (purchase) draft.purchases.set(purchase.id, { ...purchase, status: 'delivered' })
      return
    }

    case 'TimerStarted': {
      const member = draftMemberDay(draft, event.day, event.payload.memberId)
      member.timerEndsAt = event.payload.endsAt
      member.timerMinutes = event.payload.minutes
      return
    }

    case 'RungChanged': {
      const task = draft.tasks.get(event.payload.taskId)
      if (task) draft.tasks.set(task.id, { ...task, rung: event.payload.toRung })
      return
    }

    // Phase 2 and 3 own these; they are recorded now and read later.
    case 'PlanChangeAnnounced':
    case 'CheckRequested':
    case 'CheckResolved':
    case 'StreakAdvanced':
    case 'StreakShieldUsed':
    case 'StreakReset':
    case 'PromotionScheduled':
    case 'BadgeAwarded':
    case 'TitleAwarded':
    case 'PinChanged':
    case 'PinAttemptFailed':
    case 'PinLocked':
    case 'ParentSessionOpened':
    case 'DataExported':
    case 'DataImported':
    case 'ClockAnomalyDetected':
      return
  }
}
